import {
  SONIC_GENRES,
  SONIC_ERAS,
  SONIC_TIMBRES,
  SONIC_VIBES,
  renderSonicTraits,
  type SonicTraits,
  type SonicGenre,
  type SonicEra,
  type SonicTimbre,
  type SonicVibe,
} from "../../domain/index.js";

// pt-BR labels for the closed vocabulary. The vocabulary itself is owned by
// `@studio/domain` — these are display-only and never widen/narrow it.
const GENRE_LABELS: Record<SonicGenre, string> = {
  rock: "rock",
  metal: "metal",
  pop: "pop",
  electronic: "eletrônico",
  hiphop: "hip-hop",
  jazz: "jazz",
  funk: "funk",
  folk: "folk",
  classical: "clássico",
  rnb: "r&b",
  reggae: "reggae",
  ambient: "ambient",
};

const ERA_LABELS: Record<SonicEra, string> = {
  "60s": "anos 60",
  "70s": "anos 70",
  "80s": "anos 80",
  "90s": "anos 90",
  "2000s": "anos 2000",
  modern: "moderno",
};

const TIMBRE_LABELS: Record<SonicTimbre, string> = {
  warm: "quente",
  bright: "brilhante",
  dark: "escuro",
  gritty: "áspero",
  clean: "limpo",
  lofi: "lo-fi",
  lush: "denso",
  minimal: "minimal",
};

const VIBE_LABELS: Record<SonicVibe, string> = {
  melancholic: "melancólico",
  uplifting: "animado",
  aggressive: "agressivo",
  dreamy: "onírico",
  playful: "brincalhão",
  epic: "épico",
  nostalgic: "nostálgico",
  groovy: "groovy",
};

const ENERGY_LABELS: Record<number, string> = {
  1: "muito calmo",
  2: "relaxado",
  3: "neutro",
  4: "energético",
  5: "intenso",
};

const LIST_MAX = 3;

interface Props {
  value: SonicTraits;
  onChange: (next: SonicTraits) => void;
}

/**
 * The five-axis sonic-identity editor (genres / era / energy / timbre / vibe).
 * Shared between the artist form and the song-editor override control. Reads
 * the closed vocabulary straight from `@studio/domain` — nothing hard-coded.
 */
export function SonicTraitsEditor({ value, onChange }: Props): JSX.Element {
  const preview = renderSonicTraits(value);

  function toggleListTerm<T extends string>(axis: "genres" | "timbre" | "vibe", term: T): void {
    const current = (value[axis] ?? []) as T[];
    const has = current.includes(term);
    if (!has && current.length >= LIST_MAX) return; // max 3 — picking a 4th is a no-op
    const next = has ? current.filter((t) => t !== term) : [...current, term];
    onChange({ ...value, [axis]: next.length > 0 ? next : undefined });
  }

  return (
    <div className="flex flex-col gap-5">
      <ChipAxis
        axisNum="01"
        title="Gêneros"
        hint="até 3"
        terms={SONIC_GENRES}
        labels={GENRE_LABELS}
        selected={value.genres ?? []}
        atMax={(value.genres ?? []).length >= LIST_MAX}
        onToggle={(t) => toggleListTerm("genres", t)}
      />

      <EraAxis value={value.era} onChange={(era) => onChange({ ...value, era })} />

      <EnergyAxis value={value.energy} onChange={(energy) => onChange({ ...value, energy })} />

      <ChipAxis
        axisNum="04"
        title="Timbre"
        hint="até 3"
        terms={SONIC_TIMBRES}
        labels={TIMBRE_LABELS}
        selected={value.timbre ?? []}
        atMax={(value.timbre ?? []).length >= LIST_MAX}
        onToggle={(t) => toggleListTerm("timbre", t)}
      />

      <ChipAxis
        axisNum="05"
        title="Vibe"
        hint="até 3"
        terms={SONIC_VIBES}
        labels={VIBE_LABELS}
        selected={value.vibe ?? []}
        atMax={(value.vibe ?? []).length >= LIST_MAX}
        onToggle={(t) => toggleListTerm("vibe", t)}
      />

      <SnippetPreview text={preview} />
    </div>
  );
}

/** A read-only render of `renderSonicTraits`, framed as a tape-label printout. */
export function SnippetPreview({ text }: { text: string }): JSX.Element {
  return (
    <div
      className="border-l-2 border-oscillo bg-asphalt/60 px-3 py-3"
      aria-label="Prévia do prompt"
    >
      <p className="catalog-num mb-1.5 text-oscillo">PRÉVIA · O QUE VAI PRO LYRIA</p>
      {text ? (
        <p className="font-mono text-xs leading-relaxed text-paper">{text}</p>
      ) : (
        <p className="font-mono text-xs italic leading-relaxed text-ink-dim">
          sem traços — a geração fica neutra
        </p>
      )}
    </div>
  );
}

function AxisHeader({
  num,
  title,
  hint,
}: {
  num: string;
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-[9px] tabular-nums text-vinyl">{num}</span>
      <span className="catalog-num text-paper">{title}</span>
      {hint && (
        <span className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
          · {hint}
        </span>
      )}
    </div>
  );
}

function ChipAxis<T extends string>({
  axisNum,
  title,
  hint,
  terms,
  labels,
  selected,
  atMax,
  onToggle,
}: {
  axisNum: string;
  title: string;
  hint: string;
  terms: readonly T[];
  labels: Record<T, string>;
  selected: readonly T[];
  atMax: boolean;
  onToggle: (term: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
      <AxisHeader num={axisNum} title={title} hint={`${hint} · ${selected.length}/3`} />
      <div className="flex flex-wrap gap-1.5">
        {terms.map((term) => {
          const on = selected.includes(term);
          const locked = !on && atMax;
          return (
            <button
              key={term}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={labels[term]}
              disabled={locked}
              onClick={() => onToggle(term)}
              className={`border px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest2 transition-colors ${
                on
                  ? "border-butter bg-butter/15 text-butter"
                  : locked
                    ? "cursor-not-allowed border-border/50 text-ink-dim"
                    : "border-border text-ink-muted hover:border-paper hover:text-paper"
              }`}
            >
              {labels[term]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EraAxis({
  value,
  onChange,
}: {
  value: SonicEra | undefined;
  onChange: (era: SonicEra | undefined) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
      <AxisHeader num="02" title="Época" hint="um só" />
      <div className="flex flex-wrap gap-1.5">
        {SONIC_ERAS.map((era) => {
          const on = value === era;
          return (
            <button
              key={era}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={ERA_LABELS[era]}
              // Clicking the active era again clears it (era is optional).
              onClick={() => onChange(on ? undefined : era)}
              className={`border px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest2 transition-colors ${
                on
                  ? "border-butter bg-butter/15 text-butter"
                  : "border-border text-ink-muted hover:border-paper hover:text-paper"
              }`}
            >
              {ERA_LABELS[era]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EnergyAxis({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (energy: number | undefined) => void;
}): JSX.Element {
  // `undefined` reads as the neutral centre on the fader; the "definir" toggle
  // distinguishes "no energy declared" from "energy = 3 (neutral)".
  const declared = value !== undefined;
  const shown = value ?? 3;
  return (
    <div className="flex flex-col gap-2 border-l-2 border-border pl-3">
      <div className="flex items-baseline justify-between">
        <AxisHeader num="03" title="Energia" />
        <button
          type="button"
          aria-pressed={declared}
          onClick={() => onChange(declared ? undefined : 3)}
          className={`font-mono text-[9px] uppercase tracking-widest2 transition-colors ${
            declared ? "text-butter hover:text-paper" : "text-ink-dim hover:text-paper"
          }`}
        >
          {declared ? "× limpar" : "+ definir"}
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={shown}
          aria-label="Energia"
          aria-valuetext={ENERGY_LABELS[shown]}
          disabled={!declared}
          onChange={(e) => onChange(Number(e.target.value))}
          className="fader flex-1"
        />
        <span
          className={`w-28 text-right font-mono text-[10px] uppercase tracking-widest2 ${
            declared ? "text-butter" : "text-ink-dim"
          }`}
        >
          {declared ? `${String(shown)} · ${ENERGY_LABELS[shown]}` : "não definida"}
        </span>
      </div>
      <div className="flex justify-between px-0.5 font-mono text-[8px] uppercase tracking-widest2 text-ink-dim">
        <span>calmo</span>
        <span>neutro</span>
        <span>intenso</span>
      </div>
    </div>
  );
}
